import { cineamoApiClient } from '../../apiClient';
import { RequestConfig } from '../../fetch-helper';
import { UrlQueryParameter, buildUrlQuery } from '../../helpers/url-query-helper';
import { SeatCategoryDto } from '../../models/seat-category/SeatCategoryDto.types';
import { SeatCategoryInputDto } from '../../models/seat-category/SeatCategoryInputDto.types';
import { ApiErrorResponse } from '../../types/ApiErrorResponse.types';
import { SeatCategoryListResponse } from '../../types/ApiListResponse.types';
import { ApiResponse } from '../../types/ApiResponse.types';

// <------------------------ Single Seat Category ------------------------>

/**
 * This endpoint returns a single seatCategory.
 *
 * @example
 * // Get the details of a single seatCategory by seatCategoryId or slug:
 * getSeatCategoryById(1);
 *
 * @param id
 * @returns {ApiResponse<SeatCategoryDto>}
 */

export async function getSeatCategoryById(id: number | string): Promise<ApiResponse<SeatCategoryDto>> {
    return cineamoApiClient
        .get<ApiResponse<SeatCategoryDto>>('/seat-categories/' + id)
        .then((response) => response.data)
        .catch(() => null);
}

// <------------------------ Seat Categories ------------------------>

export type GetSeatCategoriesQuery = {
    cinemaId?: number;
} & UrlQueryParameter;

/**
 * This endpoint returns a filtered list of seat-categories.
 *
 * @example
 * // Get a list seat-categories filtered according to query parameters used:
 * getSeatCategories({isActive: true});
 *
 * @returns {ApiResponse<SeatCategoryListResponse>}
 */

export async function getSeatCategories(
    params?: GetSeatCategoriesQuery,
    config?: RequestConfig
): Promise<ApiResponse<SeatCategoryListResponse>> {
    return cineamoApiClient
        .get<ApiResponse<SeatCategoryListResponse>>(`/seat-categories${buildUrlQuery(params)}`, config)
        .then((response) => response.data)
        .catch(() => null);
}

// <------------------------ Create SeatCategory ------------------------>

/**
 * This endpoint creates a single seatCategory and returns a seatCategory object or an error.
 *
 * @example
 * // Create a single seatCategory from form data:
 * createSeatCategory({...seatCategory});
 *
 * @param seatCategory
 * @returns {ApiResponse<SeatCategoryDto> | ApiErrorResponse<SeatCategoryDto>}
 */

export async function createSeatCategory(
    seatCategory: SeatCategoryInputDto
): Promise<ApiResponse<SeatCategoryDto> | ApiErrorResponse<SeatCategoryInputDto>> {
    return cineamoApiClient
        .post('/seat-categories', seatCategory)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Update SeatCategory ------------------------>

/**
 * This endpoint updates a single seatCategory and returns the updated seatCategory object or an error.
 *
 * @example
 * // Update the details of a single seatCategory from form data:
 * updateSeatCategoryById({priceChange: 456});
 *
 * @param seatCategory
 * @param seatCategoryId
 * @returns {ApiResponse<SeatCategoryDto>}
 */

export async function updateSeatCategoryById(
    seatCategoryId: number,
    seatCategory: SeatCategoryInputDto
): Promise<ApiResponse<SeatCategoryDto> | ApiErrorResponse<SeatCategoryInputDto>> {
    return cineamoApiClient
        .patch(`/seat-categories/${seatCategoryId}`, seatCategory)
        .then((res) => res.data)
        .catch((error) => error);
}

// <------------------------ Delete Seat Category ------------------------>

/**
 * This endpoint returns a boolean.
 *
 * @example
 * // Delete a single seat category by id:
 * deleteSeatCategoryById(1);
 *
 * @param seatCategoryId
 * @returns {boolean}
 */

export async function deleteSeatCategoryById(seatCategoryId: number | string): Promise<boolean> {
    return cineamoApiClient
        .delete(`/seat-categories/${seatCategoryId}`)
        .then((response) => response.status === 204)
        .catch(() => false);
}
